import { PointerEvent as ReactPointerEvent, useRef, useState } from 'react';
import { useI18n } from '../i18n';

interface Props {
  onEnd: () => void;
  busy?: boolean;
}

const COMMIT_PCT = 85;
const THUMB_PX = 44;
const CONFIRM_MS = 3000;

// Design brief §8.2: replaces the "End check-in now" confirm dialog. Drag
// the thumb past 85% of the track to end (less springs back) — or press it
// once for a 3-second "press again to end" window, a second press ending
// without any drag at all. Both paths call the same onEnd.
export function SlideToEnd({ onEnd, busy }: Props) {
  const { t } = useI18n();
  const trackRef = useRef<HTMLDivElement>(null);
  const [pct, setPct] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const moved = useRef(false);
  const pointerId = useRef<number | null>(null);
  const confirmTimer = useRef<number | null>(null);

  function trackMetrics() {
    const rect = trackRef.current?.getBoundingClientRect();
    return { left: rect?.left ?? 0, width: rect?.width || THUMB_PX * 4 };
  }

  function onPointerDown(e: ReactPointerEvent<HTMLButtonElement>) {
    if (busy) return;
    pointerId.current = e.pointerId;
    moved.current = false;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDragging(true);
  }

  function onPointerMove(e: ReactPointerEvent<HTMLButtonElement>) {
    if (pointerId.current !== e.pointerId || !dragging) return;
    const { left, width } = trackMetrics();
    const next = Math.min(100, Math.max(0, ((e.clientX - left) / width) * 100));
    if (Math.abs(next - pct) > 1) moved.current = true;
    setPct(next);
  }

  function endDrag() {
    if (!dragging) return;
    setDragging(false);
    pointerId.current = null;
    if (pct >= COMMIT_PCT) {
      setPct(100);
      onEnd();
    } else {
      setPct(0);
    }
  }

  function onPointerUp() {
    endDrag();
  }
  function onPointerCancel() {
    setDragging(false);
    pointerId.current = null;
    setPct(0);
  }

  function onClick() {
    // A real drag already decided the outcome via pointerup; the browser's
    // own click that follows it is not a second, independent press.
    if (moved.current) {
      moved.current = false;
      return;
    }
    if (confirming) {
      if (confirmTimer.current) window.clearTimeout(confirmTimer.current);
      setConfirming(false);
      onEnd();
      return;
    }
    setConfirming(true);
    confirmTimer.current = window.setTimeout(() => setConfirming(false), CONFIRM_MS);
  }

  const { width: trackWidth } = trackMetrics();
  const thumbLeft = Math.min(trackWidth - THUMB_PX, (pct / 100) * (trackWidth - THUMB_PX));

  return (
    <div className={`slide-end-track${dragging ? ' dragging' : ''}`} ref={trackRef}>
      <div className="slide-end-fill" style={{ width: `${Math.max(pct, 0)}%` }} aria-hidden="true" />
      <span className="slide-end-label" aria-hidden="true">
        {confirming ? t['ts.pressAgainToEnd'] : t['ts.slideToEnd']}
      </span>
      <button
        type="button"
        className="slide-end-thumb"
        style={{ left: `${thumbLeft}px`, transition: dragging ? 'none' : undefined }}
        disabled={busy}
        aria-label={confirming ? t['ts.pressAgainToEnd'] : t['ts.slideToEndAria']}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onClick={onClick}
      >
        <span aria-hidden="true">→</span>
      </button>
    </div>
  );
}
